import { ABBREVIATION_WHITELIST } from './spamWordList';

export interface PatternHit {
  type:
    | 'excessive_exclamations'
    | 'all_caps_run'
    | 'too_many_emojis'
    | 'subject_too_short'
    | 'subject_too_long'
    | 'high_link_density'
    | 'image_only_body'
    | 'extreme_percentage_promise';
  severity: 'red' | 'orange' | 'yellow';
  message: string;
  penalty: number;
  location: 'subject' | 'preheader' | 'body';
  match?: string;
}

const HTML_ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&nbsp;': ' ',
  '&auml;': 'ä',
  '&ouml;': 'ö',
  '&uuml;': 'ü',
  '&Auml;': 'Ä',
  '&Ouml;': 'Ö',
  '&Uuml;': 'Ü',
  '&szlig;': 'ß',
  '&euro;': '€',
  '&ndash;': '–',
  '&mdash;': '—',
  '&hellip;': '…',
  '&bdquo;': '„',
  '&ldquo;': '“',
  '&rdquo;': '”',
  '&shy;': '',
};

function decodeHtmlEntities(input: string): string {
  let result = input;
  for (const [entity, char] of Object.entries(HTML_ENTITIES)) {
    result = result.split(entity).join(char);
  }
  result = result.replace(/&#(\d+);/g, (_, num) => String.fromCharCode(parseInt(num, 10)));
  result = result.replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
  return result;
}

export function htmlToPlainText(html: string): string {
  if (!html) return '';

  let text = html
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '');

  text = text.replace(/<\/(p|div|h[1-6]|li|tr|br)[^>]*>/gi, '\n').replace(/<br\s*\/?>/gi, '\n');

  text = text.replace(/<[^>]+>/g, '');
  text = decodeHtmlEntities(text);

  text = text
    .replace(/[ \t]+/g, ' ')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text;
}

export function countExclamationRuns(text: string): number {
  if (!text) return 0;
  const matches = text.match(/!{3,}/g);
  return matches ? matches.length : 0;
}

export function findAllCapsRuns(text: string, minLength = 6): string[] {
  if (!text) return [];
  const whitelist = new Set(ABBREVIATION_WHITELIST.map((w) => w.toUpperCase()));
  const matches: string[] = [];
  const regex = /[A-ZÄÖÜ]{6,}(?:\s+[A-ZÄÖÜ]{2,})*/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    const word = match[0];
    const tokens = word.split(/\s+/);
    const filtered = tokens.filter((t) => !whitelist.has(t.toUpperCase()));
    if (filtered.length === 0) continue;
    const reconstructed = filtered.join(' ');
    if (reconstructed.length >= minLength) {
      matches.push(word);
    }
  }
  return matches;
}

const EMOJI_REGEX =
  /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{27BF}]|[\u{1F000}-\u{1F02F}]|[\u{1F0A0}-\u{1F0FF}]|[\u{1F100}-\u{1F1FF}]|[\u{1F200}-\u{1F2FF}]|[\u{2300}-\u{23FF}]|[\u{2B00}-\u{2BFF}]|[\u{2700}-\u{27BF}]/gu;

export function countEmojis(text: string): number {
  if (!text) return 0;
  const matches = text.match(EMOJI_REGEX);
  return matches ? matches.length : 0;
}

export function countLinks(htmlOrText: string): number {
  if (!htmlOrText) return 0;
  const htmlLinks = (htmlOrText.match(/<a\s+[^>]*href=/gi) || []).length;
  if (htmlLinks > 0) return htmlLinks;
  const urlMatches = htmlOrText.match(/https?:\/\/[^\s<>"']+/g) || [];
  return urlMatches.length;
}

export function assessSubjectLength(subject: string): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!subject || subject.trim().length === 0) return hits;
  const length = subject.trim().length;
  if (length < 15) {
    hits.push({
      type: 'subject_too_short',
      severity: 'yellow',
      message: `Betreff nur ${length} Zeichen (optimal: 30-50)`,
      penalty: 5,
      location: 'subject',
    });
  } else if (length > 70) {
    const over = length - 70;
    const penalty = Math.min(10, Math.ceil(over / 10) * 5);
    hits.push({
      type: 'subject_too_long',
      severity: 'yellow',
      message: `Betreff ${length} Zeichen (optimal: 30-50, Mobile zeigt ~50)`,
      penalty,
      location: 'subject',
    });
  }
  return hits;
}

export function assessExclamations(
  text: string,
  location: 'subject' | 'preheader' | 'body',
): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!text) return hits;
  const runs = countExclamationRuns(text);
  if (runs > 0) {
    const mult = location === 'body' ? 1 : 2;
    hits.push({
      type: 'excessive_exclamations',
      severity: location === 'body' ? 'yellow' : 'orange',
      message: `${runs}× drei oder mehr Ausrufezeichen hintereinander`,
      penalty: 10 * runs * mult,
      location,
    });
  }
  return hits;
}

export function assessCapsRuns(
  text: string,
  location: 'subject' | 'preheader' | 'body',
): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!text) return hits;
  const runs = findAllCapsRuns(text);
  for (const run of runs) {
    hits.push({
      type: 'all_caps_run',
      severity: location === 'subject' ? 'orange' : 'yellow',
      message: `Großbuchstaben-Run "${run}"`,
      penalty: location === 'subject' ? 10 : 5,
      location,
      match: run,
    });
  }
  return hits;
}

export function assessEmojis(subject: string): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!subject) return hits;
  const count = countEmojis(subject);
  if (count >= 3) {
    hits.push({
      type: 'too_many_emojis',
      severity: 'orange',
      message: `${count} Emojis im Betreff (1 ok, 3+ kritisch)`,
      penalty: 10,
      location: 'subject',
    });
  }
  return hits;
}

export function assessLinkDensity(htmlBody: string): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!htmlBody) return hits;
  const plainText = htmlToPlainText(htmlBody);
  const textLen = plainText.length;
  if (textLen < 50) return hits;
  const linkCount = countLinks(htmlBody);
  const density = linkCount / (textLen / 100);
  if (density > 2) {
    hits.push({
      type: 'high_link_density',
      severity: 'orange',
      message: `${linkCount} Links auf ${textLen} Zeichen Text (>2 je 100 Zeichen)`,
      penalty: 10,
      location: 'body',
    });
  } else if (density > 1) {
    hits.push({
      type: 'high_link_density',
      severity: 'yellow',
      message: `${linkCount} Links auf ${textLen} Zeichen Text (>1 je 100 Zeichen)`,
      penalty: 5,
      location: 'body',
    });
  }
  return hits;
}

// Matches percentage claims ≥100% (scam-typical in financial marketing).
// Accepts German thousands-separator (3.105 %) and bare 3+ digit numbers (+3105%).
// Skips legitimate small percentages like "8,2%", "2,5 %", "99 %".
const EXTREME_PERCENT_REGEX =
  /\+?\s*(?:\d{1,3}[.,]\d{3}(?:[.,]\d+)?|\d{3,}(?:[.,]\d+)?)\s*(?:%|Prozent)/gi;

export function assessExtremePercentagePromises(
  text: string,
  location: 'subject' | 'preheader' | 'body',
): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!text) return hits;
  const matches = text.match(EXTREME_PERCENT_REGEX);
  if (!matches || matches.length === 0) return hits;
  const seen = new Set<string>();
  for (const raw of matches) {
    const trimmed = raw.trim();
    if (seen.has(trimmed)) continue;
    seen.add(trimmed);
    const penalty = location === 'body' ? 10 : 15;
    hits.push({
      type: 'extreme_percentage_promise',
      severity: 'red',
      message: `Extremes Renditeversprechen "${trimmed}" — Hauptsignal für Spam-Filter`,
      penalty,
      location,
      match: trimmed,
    });
  }
  return hits;
}

export function assessImageOnlyBody(htmlBody: string): PatternHit[] {
  const hits: PatternHit[] = [];
  if (!htmlBody) return hits;
  const plainText = htmlToPlainText(htmlBody);
  const imgMatches = htmlBody.match(/<img\s[^>]*>/gi);
  const imgCount = imgMatches ? imgMatches.length : 0;
  if (imgCount > 0 && plainText.length < 200) {
    hits.push({
      type: 'image_only_body',
      severity: 'orange',
      message: `Body fast nur Bilder (${imgCount} Bilder, ${plainText.length} Zeichen Text)`,
      penalty: 15,
      location: 'body',
    });
  }
  return hits;
}
